'use client'

import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LifeBuoy, BookOpenCheck, HelpCircle, Search, Mail, Settings, ArrowRight, Terminal, ScrollText, ShieldCheck, BookA } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'

const resources = [
  {
    title: "Resource Guides",
    description: "In-depth guides covering getting started, RFP processes, proposal writing, and compliance.",
    href: "/resources/guides",
    icon: BookOpenCheck
  },
  {
    title: "Frequently Asked Questions (FAQ)",
    description: "Find quick answers to common questions about the platform, billing, features, and more.",
    href: "/resources/faq",
    icon: HelpCircle
  },
  {
    title: "Platform Features Overview",
    description: "Explore the key features and capabilities of the GovCon Data platform.",
    href: "/features",
    icon: Settings
  },
  {
    title: "API Documentation",
    description: "Integrate with GovCon Data using our developer APIs. Access endpoints and usage guides.",
    href: "/api-docs",
    icon: Terminal
  },
  {
    title: "Changelog",
    description: "See the latest updates, improvements, and feature releases for the platform.",
    href: "/changelog",
    icon: ScrollText
  },
  {
    title: "Security Overview",
    description: "Learn about our security practices, data protection measures, and compliance.",
    href: "/security",
    icon: ShieldCheck
  },
  {
    title: "GovCon Glossary",
    description: "Browse 250+ government contracting terms, acronyms, and definitions from A to Z.",
    href: "/glossary",
    icon: BookA
  },
  {
    title: "Contact Support",
    description: "Can't find what you need? Get in touch with our support team for assistance.",
    href: "/contact",
    icon: Mail
  }
]

export default function DocumentationPage() {
  return (
    <div className="bg-gradient-to-b from-background to-muted/30 min-h-screen py-16 lg:py-24 dashboard-cards">
      <div className="container max-w-6xl mx-auto px-4">

        {/* Header Section */}
        <div className="text-center mb-12 lg:mb-16">
          <LifeBuoy className="h-16 w-16 text-primary mx-auto mb-6" />
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">Documentation & Support</h1>
          <p className="text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
            Your central hub for guides, FAQs, feature information, API docs, and getting help with the GovCon Data platform.
          </p>
        </div>

        {/* Resource Links Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mt-12">
          {resources.map((resource) => (
            <Link key={resource.title} href={resource.href} className="block group">
              <Card className="h-full bg-card/80 backdrop-blur-sm border rounded-lg shadow-md hover:shadow-lg hover:border-primary/40 transition-all duration-300 p-1">
                <CardHeader className="flex flex-row items-center gap-4">
                  <div className="p-3 rounded-md bg-gradient-to-br from-primary/10 to-transparent border flex-shrink-0">
                    <resource.icon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-grow">
                    <CardTitle className="text-lg mb-1 group-hover:text-primary transition-colors">{resource.title}</CardTitle>
                    <CardDescription className="text-sm leading-snug">{resource.description}</CardDescription>
                  </div>
                  <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground flex-shrink-0 group-hover:translate-x-1 group-hover:text-primary transition-all duration-200"/>
                </CardHeader>
              </Card>
            </Link>
          ))}
        </div>

      </div>
    </div>
  )
} 